import { FC, SyntheticEvent } from 'react';
import { SpotButton, SpotTextInput } from '@enterprise/spot';
import { Col, Row } from 'react-bootstrap';
import { Field, Form } from 'react-final-form';
import { FormRenderProps } from '@enterprise/common';
import i18n from 'i18next';
import { MappingTypes } from '../../../../core/enums/Mapping';
import { useStores } from '../../../../hooks';
import { observer } from 'mobx-react';
import { length, notStartWith } from '../../../../core/helpers';
import { EditableClassificationFields } from '../../MasterDataPageStore';

interface ClassificationEditFieldsProps {
    isEdit?: boolean;
    onCancel: (event: SyntheticEvent) => void;
    onSuccess: () => void;
    parentId?: string;
}

export const ClassificationEditFields: FC<ClassificationEditFieldsProps> = observer(function ({
    isEdit = false,
    onCancel,
    onSuccess,
    parentId,
}: ClassificationEditFieldsProps) {
    const {
        domain: { masterDataPageStore },
    } = useStores();

    const onSubmit = async (values: EditableClassificationFields): Promise<{ [key: string]: string } | void> => {
        const isValidId = await masterDataPageStore.isAvailableClassificationId(values.classId, parentId);
        if (!isValidId) {
            return {
                classId: i18n.t('masterData:classificationsPage.classificationExistsError', 'This ID is already in use.'),
            };
        }
        if (!isEdit) {
            await masterDataPageStore.createNewMasterFromFormValues(values, MappingTypes.CLASSIFICATION, parentId);
        }
        onSuccess();
    };

    return (
        <Form onSubmit={onSubmit}>
            {({ dirtySinceLastSubmit, handleSubmit, submitting, submitErrors }: FormRenderProps<EditableClassificationFields>) => (
                <div className="editable-fields">
                    <Row>
                        <Col xs={4}>
                            <Field
                                name="classId"
                                validate={(val) => {
                                    const name = i18n.t('controlCenter:classificationsPage.classificationId', 'Classification ID');
                                    return (
                                        length(name, { min: 3, max: 7 })(val) || //
                                        notStartWith(name, { pattern: '^\\s' })
                                    );
                                }}
                            >
                                {({ input, meta }) => (
                                    <div>
                                        <label className="spot-form__label">{i18n.t('masterData:classificationsPage.id', 'ID')}</label>
                                        <SpotTextInput {...input} error={meta} />
                                        {!submitting && !dirtySinceLastSubmit && submitErrors && (
                                            <span className="spot-form__field-error" role="alert">
                                                <span className="spot-form__field-error-text"> {submitErrors.classId} </span>
                                            </span>
                                        )}
                                    </div>
                                )}
                            </Field>
                        </Col>
                        <Col xs={8}>
                            <Field
                                name="classDescription"
                                validate={(val) => {
                                    const name = i18n.t('masterData:classificationsPage.classificationDescription', 'Description');
                                    return (
                                        length(name, { min: 1, max: 100 })(val) || //
                                        notStartWith(name, { pattern: '^\\s' })
                                    );
                                }}
                            >
                                {({ input, meta }) => (
                                    <div>
                                        <label className="spot-form__label">
                                            {i18n.t('masterData:classificationsPage.classificationDescription', 'Description')}
                                        </label>
                                        <SpotTextInput {...input} error={meta} />
                                    </div>
                                )}
                            </Field>
                        </Col>
                    </Row>

                    <Row className={'footer'}>
                        <Col>
                            <button className="spot-button spot-button--link" onClick={onCancel}>
                                {i18n.t('buttons:cancel', 'Cancel')}
                            </button>
                            <SpotButton disabled={submitting} isPrimary={true} onClick={handleSubmit}>
                                {isEdit ? i18n.t('buttons:save', 'Save') : i18n.t('buttons:create', 'Create')}
                            </SpotButton>
                        </Col>
                    </Row>
                </div>
            )}
        </Form>
    );
});
